import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';

// =====================================================
// INTERFACES
// =====================================================

export interface CourierCompany {
  id: string;
  name: string;
  business_registration: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  is_active: boolean;
  verification_status: 'pending' | 'verified' | 'rejected';
  verification_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CourierLocker {
  id: string;
  courier_id: string;
  locker_id: string;
  ownership_type: 'owned' | 'rented';
  rental_start_date?: string;
  rental_end_date?: string;
  rental_fee_per_month?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  lockers?: {
    locker_number: string;
    status: string;
    locations: {
      name: string;
      address: string;
      city: string;
    };
    locker_sizes: {
      name: string;
      hourly_rate: number;
      daily_rate: number;
    };
  };
}

export interface ParcelDelivery {
  id: string;
  courier_id: string;
  locker_id: string;
  tracking_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  parcel_description: string;
  parcel_weight_kg: number;
  delivery_instructions?: string;
  status: 'in_transit' | 'delivered_to_locker' | 'collected' | 'expired' | 'returned';
  delivered_at?: string;
  collected_at?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  // Joined data
  lockers?: {
    locker_number: string;
    locations: {
      name: string;
      address: string;
      city: string;
    };
  };
  collection_tokens?: {
    token_code: string;
    expires_at: string;
    is_used: boolean;
  }[];
}

export interface CreateParcelDeliveryData {
  lockerId: string;
  trackingNumber: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  parcelDescription: string;
  parcelWeightKg: number;
  deliveryInstructions?: string;
  expiresInHours?: number;
}

export interface CourierAccessData {
  accessType: 'open' | 'close' | 'deliver_parcel';
  deliveryId?: string;
}

// =====================================================
// CONTEXT TYPE
// =====================================================

interface CourierContextType {
  // State
  courierCompany: CourierCompany | null;
  availableLockers: CourierLocker[];
  deliveries: ParcelDelivery[];
  isLoading: boolean;
  error: string | null;

  // Actions
  registerCompany: (data: Omit<CourierCompany, 'id' | 'created_at' | 'updated_at' | 'verification_status' | 'is_active'>) => Promise<CourierCompany>;
  getAvailableLockers: () => Promise<void>;
  createParcelDelivery: (data: CreateParcelDeliveryData) => Promise<ParcelDelivery>;
  getDeliveries: () => Promise<void>;
  accessLocker: (lockerId: string, data: CourierAccessData) => Promise<any>;
  clearError: () => void;
}

// =====================================================
// CONTEXT CREATION
// =====================================================

const CourierContext = createContext<CourierContextType | undefined>(undefined);

// =====================================================
// PROVIDER COMPONENT
// =====================================================

interface CourierProviderProps {
  children: ReactNode;
}

export const CourierProvider: React.FC<CourierProviderProps> = ({ children }) => {
  const [courierCompany, setCourierCompany] = useState<CourierCompany | null>(null);
  const [availableLockers, setAvailableLockers] = useState<CourierLocker[]>([]);
  const [deliveries, setDeliveries] = useState<ParcelDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // =====================================================
  // COURIER COMPANY REGISTRATION
  // =====================================================

  const registerCompany = useCallback(async (data: Omit<CourierCompany, 'id' | 'created_at' | 'updated_at' | 'verification_status' | 'is_active'>): Promise<CourierCompany> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.COURIER.BASE}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Company registration failed');
      }

      setCourierCompany(result.company);
      return result.company;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Company registration failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // GET AVAILABLE LOCKERS
  // =====================================================

  const getAvailableLockers = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_ENDPOINTS.COURIER.BASE}/lockers/available`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch available lockers');
      }

      setAvailableLockers(result.lockers || []);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch available lockers';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // CREATE PARCEL DELIVERY
  // =====================================================

  const createParcelDelivery = useCallback(async (data: CreateParcelDeliveryData): Promise<ParcelDelivery> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_ENDPOINTS.COURIER.DELIVERIES}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create parcel delivery');
      }

      // Add the new delivery to the list
      setDeliveries(prev => [result.delivery, ...prev]);

      return result.delivery;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create parcel delivery';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // GET DELIVERIES
  // =====================================================

  const getDeliveries = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_ENDPOINTS.COURIER.DELIVERIES}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch deliveries');
      }

      setDeliveries(result.deliveries || []);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch deliveries';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // ACCESS LOCKER
  // =====================================================

  const accessLocker = useCallback(async (lockerId: string, data: CourierAccessData): Promise<any> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/couriers/lockers/${lockerId}/access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to access locker');
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to access locker';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =====================================================
  // UTILITY FUNCTIONS
  // =====================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value: CourierContextType = {
    // State
    courierCompany,
    availableLockers,
    deliveries,
    isLoading,
    error,

    // Actions
    registerCompany,
    getAvailableLockers,
    createParcelDelivery,
    getDeliveries,
    accessLocker,
    clearError,
  };

  return (
    <CourierContext.Provider value={value}>
      {children}
    </CourierContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useCourier = (): CourierContextType => {
  const context = useContext(CourierContext);
  if (context === undefined) {
    throw new Error('useCourier must be used within a CourierProvider');
  }
  return context;
};
